// Copyright (c) Docugami, Inc. All rights reserved.
import { ComparisonUnitAtom } from "./ComparisonUnitAtom";
import { DgXmlNames } from "./DgXmlNames";
import { ComparisonUnitToken } from "./ComparisonUnitToken";
import { CorrelatedSequence } from "./CorrelatedSequence";
import { CorrelationStatus } from "./CorrelationStatus";
import { getDocugamiIdFromPElement } from "./DgParagraphMetadataUtil";
import { DgWordCanvas } from "./DgWordCanvas";
import { LcsSettings } from "./LcsSettings";
import { DOMParser } from '@xmldom/xmldom'
import { StringBuilder } from "utils/StringBuilder";
import { Logger, LoggerCategory } from "utils/Logger";

/*eslint-disable */

// Note: the following const have eslint-disable because they form the whole of the Open-Xml-Sdk for JavaScript API.
// Most modules will use only a subset of the API, but disabling portions of the API leads to friction when writing
// code.  Disabling eslint for these variables removes the error indicating that a variable is assigned a value but
// never used.  If we ever re-write these modules in TypeScript, then the public API will change significantly.

const Enumerable = require("../../../../../utils/linq");
const Ltxml = require('../../../../../utils/ltxml.js');
Ltxml.DOMParser = DOMParser

const openXml = require('../../../../../utils/openxml');

const XAttribute = Ltxml.XAttribute;
const XCData = Ltxml.XCData;
const XComment = Ltxml.XComment;
const XContainer = Ltxml.XContainer;
const XDeclaration = Ltxml.XDeclaration;
const XDocument = Ltxml.XDocument;
const XElement = Ltxml.XElement;
const XName = Ltxml.XName;
const XNamespace = Ltxml.XNamespace;
const XNode = Ltxml.XNode;
const XObject = Ltxml.XObject;
const XProcessingInstruction = Ltxml.XProcessingInstruction;
const XText = Ltxml.XText;
const XEntity = Ltxml.XEntity;
const cast = Ltxml.cast;
const castInt = Ltxml.castInt;

const W = openXml.W;
const W14 = openXml.W14;
const W15 = openXml.W15;
const NN = openXml.NoNamespace;
const wNs = openXml.wNs;
/*eslint-enable */

export function dgGetParagraphsChunksTokens(dgWordCanvas: DgWordCanvas, spanIdList: string[]): any
{
    const correlatedSequenceList: CorrelatedSequence[] = dgWordCanvas.correlatedSequenceList as CorrelatedSequence[];
    const paragraphsChunksTokens: any = [];

    const allPairs = Enumerable.from(correlatedSequenceList)
        .where((correlatedSequence: any) => correlatedSequence.correlationStatus === CorrelationStatus.Equal)
        .select((correlatedSequence: any) => {
            return Enumerable.from(correlatedSequence.comparisonUnitArray1)
                .zip(Enumerable.from(correlatedSequence.comparisonUnitArray2),
                    (pu1: ComparisonUnitToken, pu2: ComparisonUnitToken) =>
                    {
                        return {
                            pu1: pu1,
                            pu2: pu2,
                        };
                    });
        })
        .selectMany((m: any) => m)
        .toArray();

    if (LcsSettings.lcsDebugFalse)
    {
        const sb = new StringBuilder();
        sb.appendLine("allPairs");
        sb.appendLine("========");
        allPairs.forEach((pair: any) =>
        {
            const dgmlToken = pair.pu2 as ComparisonUnitToken;
            const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
            const dgmlTreeElement = dgmlFirstAtom.contentElement;
            const dgmlSpanIds = dgmlTreeElement
                .ancestorsAndSelf()
                .reverse()
                .select((a: any) => cast(a.attribute(DgXmlNames.spanId)))
                .toArray();

            sb.appendLine(`>${dgmlToken.tokenString}< Span Ids: ${dgmlSpanIds}`);
        })
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    for (const pair of allPairs)
    {
        const dgmlToken = pair.pu2 as ComparisonUnitToken;

        // It isnt just the spanId at the leaf.  It can match any of the ancestors.
        const dgmlFirstAtom = dgmlToken.contents[0] as ComparisonUnitAtom;
        const dgmlTreeElement = dgmlFirstAtom.contentElement;
        const relevantChunkList = dgmlTreeElement
            .ancestorsAndSelf()
            .where((de: any) =>
                {
                    const spanId = de.attribute(DgXmlNames.spanId)?.value || "";
                    const foundAny = Enumerable.from(spanIdList).any((fs: string) => fs === spanId);
                    return foundAny;
                });

        const relevantChunk = relevantChunkList.firstOrDefault();

        const docxToken = pair.pu1 as ComparisonUnitToken;

        // have the guid on the docxToken
        const docxFirstAtom = docxToken.contents[0] as ComparisonUnitAtom;
        const docxTreeElement = docxFirstAtom.contentElement;
        const para = docxTreeElement.ancestorsAndSelf(W.p).firstOrDefault();
        const paraUuid = getDocugamiIdFromPElement(para);

        if (relevantChunk === null) {
            if (paragraphsChunksTokens.length > 0 && !(paragraphsChunksTokens[paragraphsChunksTokens.length - 1].NextParaGuid)) {
                paragraphsChunksTokens[paragraphsChunksTokens.length - 1].NextParaGuid = paraUuid;
            }
            continue;
        }

        const relevantSpanId = relevantChunk.attribute(DgXmlNames.spanId).value;

        const newItem =
        {
            ParagraphUuid: paraUuid,
            DgmlSpanId: relevantSpanId,
            Token: dgmlToken.tokenString,
        };

        if (paragraphsChunksTokens.length > 0 && !(paragraphsChunksTokens[paragraphsChunksTokens.length - 1].NextParaGuid)) {
            paragraphsChunksTokens[paragraphsChunksTokens.length - 1].NextParaGuid = paraUuid;
        }
        paragraphsChunksTokens.push(newItem);
    }

    if (LcsSettings.lcsDebugFalse) {
        const sb = new StringBuilder();
        sb.appendLine("ParagraphChunksTokens");
        sb.appendLine("=====================");
        paragraphsChunksTokens.forEach((pct: any) => sb.appendLine(`Para: ${pct.ParagraphUuid} Dgml: ${pct.DgmlSpanId} Token: >${pct.Token}<`));
        Logger.LogTrace(LoggerCategory.AssistedAuthoring, sb.toString());
    }

    return paragraphsChunksTokens;
}
